import React, { useEffect, useMemo, useState } from 'react';
import Button from 'components/Button';
import Form, { FormSubmitHandler } from 'components/Form/Form';
import FormInput from 'components/Form/FormInput';
import SocialLoginButtons from 'components/SocialLogin';
import Typography from 'components/Typography';

import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { FormValidator } from 'components/Form/FormValidator';

import styles from './LoginForm.module.scss';

type FormFields = {
  username: string;
  password: string;
};

const defaultValues: FormFields = {
  username: '',
  password: '',
};

interface LoginIdentifier {
  username?: string;
  email_address?: string;
}

interface SocialLoginMessageData {
  action: string;
  status: string;
  tracking_id: string;
  cookie: string;
}

interface LoginFormProps {
  showRegForm?: Function;
  showPassResetForm?: Function;
}

const finishSocialLogin = async (data: SocialLoginMessageData) => {
  document.cookie = data.cookie;

  // timeout because it can take browser a moment to set the cookie
  return setTimeout(
    () => ({
      status: 200,
      data: {},
    }),
    20
  );
};

const LoginForm: React.FC<LoginFormProps> = ({ showRegForm, showPassResetForm }) => {
  const [loginError, setLoginError] = useState('');
  const [socialLoginError, setSocialLoginError] = useState('');

  const onLoginMessage = (message: MessageEvent) => {
    if (message.data.status === 'failure') {
      // show error message - message.data.message
      // eslint-disable-next-line no-console
      console.log('PostMessage with status=failure: ', message.data.message);

      return;
    }

    switch (message.data.action) {
      case 'login':
        finishSocialLogin(message.data).then(() => {
          window.location.reload();
        });
        break;
      case 'register':
        setSocialLoginError('Finner ikke konto. Gå tilbake og prøv igjen.');
        break;
      default:
    }
  };

  useEffect(() => {
    window.addEventListener('message', onLoginMessage);

    return () => {
      window.removeEventListener('message', onLoginMessage);
    };
  }, []);

  const onRegFormSwitch = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (showRegForm) {
      showRegForm();
    }
  };

  const onPassResetFormSwitch = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (showPassResetForm) {
      showPassResetForm();
    }
  };

  const handleSubmit: FormSubmitHandler<FormFields> = async (data): Promise<void> => {
    setLoginError('');

    const identifiers: LoginIdentifier = {};

    if (data.username.indexOf('@') === -1) {
      try {
        identifiers.username = parsePhoneNumberWithError(data.username, 'NO').format('E.164');
      } catch (e) {
        setLoginError('Angi en gyldig E-post eller telefonnummer')
        
        return;
      }
    } else {
      identifiers.email_address = data.username;
    }

    const loginCredentials = {
      identifiers,
      validators: {
        password: data.password,
      },
    };

    const response = await fetch('/blaize/login', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginCredentials),
    });

    const responseBody = await response.json();

    if (response.status !== 200) {
      if (response.status === 404) {
        setLoginError('Bruker ikke funnet');
      } else if (responseBody.message === 'Email verification is required') {
        setLoginError('E-postbekreftelse kreves');
      } else if (responseBody.message === 'Login failed') {
        setLoginError('Feil brukernavn eller passord');
      } else if (responseBody.message.match(/^User identified.*not found$/)) {
        setLoginError(
          responseBody.message.replace(
            /^User identified(.*)not found$/,
            `Finner ikke brukeren identifisert av ${data.username}`
          )
        );
      } else {
        setLoginError('Feil ved innlogging');
      }

      return;
    }

    window.location.reload();
  };

  const registerQuestion = useMemo((): string => {
    switch (window.PUBLICATION_NAME) {
      case 'kapital':
        return 'Ny til Kapital? ';
      case 'finansavisen':
      default:
        return 'Ny til FA? ';
    }
  }, []);

  return (
    <div className={styles.container}>
      <Typography variant="h3" className={styles.heading}>
        Logg inn
      </Typography>
      <Typography variant="body1" color="grey-900" className={styles.text}>
        Fyll inn dine opplysninger under for å logge inn.
      </Typography>
      <Form defaultValues={defaultValues} onSubmit={handleSubmit}>
        {({ formState: { isSubmitting, isSubmitted, isSubmitSuccessful } }) => (
          <>
            <FormInput
              name="username"
              type="text"
              label="Brukernavn (e-post eller telefon) *"
              className={styles.field}
              validate={FormValidator.isNotEmpty}
            />
            <FormInput
              name="password"
              type="password"
              label="Passord *"
              className={styles.field}
              validate={FormValidator.isNotEmpty}
            />
            {loginError && <p id="login-form-error" className={styles.formError}>{loginError}</p>}
            <Button
              id="btn-submit-login"
              type="submit"
              disabled={isSubmitting || (isSubmitted && loginError.length === 0 && isSubmitSuccessful)}
              fullWidth
              loading={isSubmitting}
            >
              {isSubmitting ? 'Logger inn' : 'Logg inn'}
            </Button>
          </>
        )}
      </Form>
      <Typography variant="body1">
        <a
          href="/reset-password"
          className={styles.forgotLink}
          onClick={onPassResetFormSwitch}
          id="btn-switch-forgot-password"
        >
          Jeg har glemt passordet
        </a>
      </Typography>
      <div className={styles.divider} />
      {socialLoginError && <p className={styles.formError}>{socialLoginError}</p>}
      <SocialLoginButtons />
      <Typography variant="body1" className={styles.registerLink}>
        {registerQuestion}
        <a href="/zephr/register" onClick={onRegFormSwitch} id="btn-switch-to-register">
          Registrer deg
        </a>
      </Typography>
    </div>
  );
};

export default LoginForm;
